import { GX_API_URL } from '@/config/env';
import { exportPreviewApi } from '@/features/data-hub/api/exportPreview.api';
import type { PastExportDetails } from '@/features/data-hub/api/pastExports.api';
import { templatesApi } from '@/features/data-hub/api/templates.api';
import { downloadAsFile } from '@/features/data-hub/lib/downloadFileUtils';
import { buildExportApiRequestString } from '@/features/data-hub/lib/ExportRequestService';
import type { PreviewGenerationContext } from '@/features/data-hub/lib/PreviewService.types';
import { pythonPreviewService } from '@/features/data-hub/lib/PythonPreviewService';

/** Export types where regeneration fetches data client-side (server only returns downloadUrl for Excel) */
export const CLIENT_SIDE_DOWNLOAD_TYPES = new Set(['csv', 'curl', 'python', 'gx_api']);

export const openGxApiUrl = (data: PastExportDetails, apiKey: string): void => {
  const baseUrl = buildExportApiRequestString({
    gxURL: GX_API_URL,
    symbols: data.symbols,
    fromValue: data.from ?? '',
    toValue: data.to || null,
    includeMetadata: data.metadata === 'true',
    userTimezone: data.timezone
  });
  const url = new URL(baseUrl);
  url.searchParams.set('token', apiKey);
  window.open(url.toString(), '_blank', 'noopener,noreferrer');
};

export const performClientSideDownload = async (
  effectiveType: string,
  data: PastExportDetails,
  apiKey: string
): Promise<void> => {
  if (effectiveType === 'python') {
    const template = await templatesApi.fetchPythonTemplate();
    const context: PreviewGenerationContext = {
      symbols: data.symbols,
      rangeStart: data.from || 'all',
      rangeEnd: data.to || '',
      timezone: data.timezone,
      includeMetadata: data.metadata === 'true',
      apiKey,
      gxURL: ''
    };
    const result = pythonPreviewService.generateFromTemplate(template, context);
    downloadAsFile(result.content, 'python');
  } else {
    const isCSV = effectiveType === 'csv';
    const content = await exportPreviewApi.fetchPreviewList(
      {
        from: data.from ?? '',
        to: data.to || null,
        includeMetadata: data.metadata === 'true',
        isCSV,
        userTimezone: data.timezone
      },
      data.symbols,
      isCSV ? { Accept: 'text/csv' } : undefined
    );
    downloadAsFile(content, isCSV ? 'csv' : 'json');
  }
};
